import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { ApplicationFormPageEnum } from '../types';
import { intermediaryFeesQuestions } from './questions';
import { IIntermediaryFeesDetails, IIntermediaryFeesForm } from './types';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { IntermediaryFeesTable } from '@pages/ApplicationFormPage/IntermediaryFees/IntermediaryFeesTable/IntermediaryFeesTable';
import React, { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { getCurrentRoute } from '@utils/location';

export const IntermediaryFees = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(id);
  const form = useForm<IIntermediaryFeesForm>({
    defaultValues: {
      hasIntermediaryFees: null,
      fees: [],
    },
  });
  const formFields = form.watch();

  useEffect(() => {
    if (!formFields.hasIntermediaryFees) form.setValue('fees', []);
  }, [formFields.hasIntermediaryFees]);

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      form.reset({
        hasIntermediaryFees: applicationInProgress[currentRoute][0].answer.length ? 'true' : 'false',
        fees: applicationInProgress[currentRoute][0].answer,
      });
    }
  }, [applicationInProgress]);

  const formDataFormatter = (formData: IIntermediaryFeesForm) => {
    return [
      {
        question: intl.formatMessage({ id: intermediaryFeesQuestions.hasIntermediaryFees.labelKey }),
        key: 'hasIntermediaryFees',
        answer: formFields.hasIntermediaryFees === 'true' ? formData.fees : [],
      },
    ];
  };

  const submitFormHandler = async (formData: IIntermediaryFeesForm) => {
    const questions = formDataFormatter(formData);

    await patchApplicationInProgress({ questions, currentRoute, applicationId: id });

    searchParams.set('section', ApplicationFormPageEnum.APPLICATION_TYPE);
    setSearchParams(searchParams);
  };

  const addFeeHandler = () => {
    const newFee: IIntermediaryFeesDetails = {
      id: uuidv4(),
      description: '',
      amount: '',
      whenPayable: null,
      howPayable: null,
      refundableAmount: '',
    };
    form.setValue('fees', [...formFields.fees, newFee]);
  };

  const removeFeeHandler = (index: number) => {
    const fees = formFields.fees.filter((fee, internalIndex) => internalIndex !== index);

    form.setValue('fees', fees);
  };

  const isNextDisabled =
    Boolean(!formFields.hasIntermediaryFees) || (formFields.hasIntermediaryFees === 'true' && !formFields.fees.length);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={intermediaryFeesQuestions.hasIntermediaryFees.labelKey} tagName={'h4'} />
          <div>
            <FormControl
              name={'hasIntermediaryFees'}
              radioOptions={intermediaryFeesQuestions.hasIntermediaryFees.options}
              radioGroupProps={{ className: 'mt-2' }}
              onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                form.setValue('hasIntermediaryFees', event.target.value);
              }}
            />
          </div>
        </div>

        {formFields.hasIntermediaryFees === 'true' && (
          <div className={'mt-2'}>
            <IntermediaryFeesTable fees={formFields.fees} onAddFee={addFeeHandler} onRemoveFee={removeFeeHandler} />
          </div>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isNextDisabled}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
